import React, { useMemo } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage } from '@fortawesome/free-solid-svg-icons';
import Placeholder from '../../images/white.png';

interface LayoutSelectorProps {
	selected: boolean;
	type: string;
	number: number;
	onClick: (num: number, layout: string, event?: React.MouseEvent<HTMLDivElement>) => any;
	onKeyDown?: (keyDownEvent: React.KeyboardEvent<HTMLDivElement>) => void;
	displayIcon?: boolean
	hasPictureInPicture?: boolean;
	layoutIndex?: number;
}

/**
 * Formats the subScript for any given layout selector
 * @param frame Current frame the selector is part of
 * @param layoutIndex Index of the selector
 * @returns String with the subScript, or blank screen when no layoutIndex provided
 */
const getLayoutSubScript = (frame: number, layoutIndex?: number): string => {
	const subScript = '';
	if (typeof layoutIndex !== 'undefined') {
		const alpha = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
		const alphaSubScript = Object.assign([], alpha);
		return `${frame}${alphaSubScript[layoutIndex]}`;
	}
	return subScript;
};

export default function LayoutSelector(props: LayoutSelectorProps) {
	const {
		type,
		selected,
		onClick,
		onKeyDown,
		number: inputNumber,
		displayIcon,
		hasPictureInPicture,
		layoutIndex,
	} = props;
	const number = useMemo(
		() => (hasPictureInPicture ? 2 : inputNumber),
		[hasPictureInPicture, inputNumber],
	);
	const subScript = getLayoutSubScript(inputNumber, layoutIndex);

	return (
		<div className={classNames('layout-selector-wrapper', { selected })}>
			<div
				onClick={(e) => onClick(number, type, e)}
				onKeyDown={onKeyDown}
				tabIndex={-1}
				className={classNames('layout-selector', type, { selected, 'video-background': hasPictureInPicture })}
			>
				<div className="video-block">
					<img alt="" src={Placeholder} />
					{displayIcon
						? (<FontAwesomeIcon icon={faImage} />)
						: (<span>{hasPictureInPicture ? 2 : 1}</span>)
					}
				</div>

				{number > 1 && !hasPictureInPicture && (
					<div className="video-block">
						<img alt="" src={Placeholder} />
						<span>2</span>
					</div>
				)}
				{number > 2 && (
					<div className="video-block">
						<img alt="" src={Placeholder} />
						<span>3</span>
					</div>
				)}
				{number > 3 && (
					<div className="video-block">
						<img alt="" src={Placeholder} />
						<span>4</span>
					</div>
				)}
				{number > 4 && (
					<div className="video-block">
						<img alt="" src={Placeholder} />
						<span>5</span>
					</div>
				)}
				{number > 5 && (
					<div className="video-block">
						<img alt="" src={Placeholder} />
						<span>6</span>
					</div>
				)}
				{number > 6 && (
					<div className="video-block">
						<img alt="" src={Placeholder} />
						<span>7</span>
					</div>
				)}
				{number > 7 && (
					<div className="video-block">
						<img alt="" src={Placeholder} />
						<span>8</span>
					</div>
				)}
				{subScript
					&& <div className="layout-selector-subscript-container">
						<div className="layout-selector-subscript">{subScript}</div>
					</div>
				}
			</div>
			{hasPictureInPicture && (
				<div className={classNames('layout-selector video-overlay', type, { selected })}>
					<div className="video-block">
						<img alt="" src={Placeholder} />
						<span>1</span>
					</div>
				</div>
			)}
		</div>
	);
}
